import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    IconButton,
    InputAdornment,
} from "@mui/material";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UpdateUserPasswordAPI } from "../../../lib/api";
import { toast } from "react-toastify";
import Cookies from "js-cookie";

const schema = yup.object().shape({
    newPassword: yup
        .string()
        .required("New Password is required")
        .min(4, "Password must be at least 4 characters"),
    confirmPassword: yup
        .string()
        .required("Confirm Password is required")
        .oneOf([yup.ref("newPassword")], "Passwords must match"),
});

const ChangePasswordModal = ({ isOpen, onClose, user }) => {
    const queryClient = useQueryClient();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
    });

    const mutation = useMutation({
        mutationFn: (data) => UpdateUserPasswordAPI(data, user.UserID),
        onSuccess: () => {
            toast.success("Password updated successfully!", { position: "top-right" });
            queryClient.invalidateQueries(["GetAllUser"]);
            handleClose();
        },
        onError: (error) => {
            toast.error(error.message || "Failed to update password", {
                position: "top-right",
            });
        },
    });

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSubmit = (data) => {
        if (!user) return;
        const CompanyId = Cookies.get("CompanyId");

        // The UpdateUserAPI expects the full updated user data.
        // If not all fields are provided, it might overwrite existing fields with blanks depending on the backend.
        // However, given the options, this mimics the structure sent in CreateUser/UpdateUser.
        const activeDept = user.Departments?.find(d => d.Status === true);
        const mappedDepId = user.DepId || activeDept?.DepID || 0;

        const payload = {
            companyId: CompanyId,
            depId: mappedDepId,
            username: user.Username,
            password: data.newPassword,
            tokenResetAllowed: user.TokenResetAllowed,
            userType: user.UserType,
            status: user.Status,
        };

        mutation.mutate(payload);
    };

    return (
        <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle className="font-bold text-gray-800 border-b">
                Change Password for {user?.Username}
            </DialogTitle>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogContent className="space-y-4 mt-4">
                    <TextField
                        fullWidth
                        label="New Password"
                        type={showPassword ? "text" : "password"}
                        {...register("newPassword")}
                        error={!!errors.newPassword}
                        helperText={errors.newPassword?.message}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        fullWidth
                        label="Confirm Password"
                        type={showConfirmPassword ? "text" : "password"}
                        {...register("confirmPassword")}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword?.message}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    >
                                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </DialogContent>
                <DialogActions className="p-4 border-t">
                    <Button onClick={handleClose} color="inherit" disabled={mutation.isPending}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={mutation.isPending}
                        className="bg-blue-600 hover:bg-blue-700"
                    >
                        {mutation.isPending ? "Saving..." : "Save Password"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ChangePasswordModal;
